import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { playChaChing } from "@/lib/chime";
import { BURST_TIMES } from "@/components/rideshare/CediWinkFace";
import { celebratePayment, isClaimed } from "@/lib/paymentCelebration";
import PaymentCelebration from "@/components/pro/PaymentCelebration";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mounted once: shows the "Payment received" slide-up for every payment a Pro receives, on any screen.
export default function PaymentCelebrationHost() {
  const { user, token } = useAuth();
  const [shown, setShown] = useState(null);
  const isPro = user?.role === "artisan";

  useEffect(() => {
    const on = (e) => setShown({ ...e.detail, at: Date.now() });
    window.addEventListener("yorm:payment-received", on);
    return () => window.removeEventListener("yorm:payment-received", on);
  }, []);

  // Withdrawals confirmed while the app was closed: celebrate on next open / return to the app.
  useEffect(() => {
    if (!isPro || !token) return undefined;
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await axios.get(`${API}/pro/wallet/celebrations?token=${token}`);
        const items = data.items || [];
        if (!items.length) return;
        const total = Math.round(items.reduce((s, i) => s + i.amount, 0) * 100) / 100;
        celebratePayment({ key: `w:${items.map((i) => i.id).join(",")}`, amount: total, method: "withdrawal", who: items[0].to });
        items.forEach((i) => axios.post(`${API}/pro/wallet/celebrations/${i.id}/seen?token=${token}`).catch(() => {}));
        window.dispatchEvent(new Event("yorm:wallet-updated"));
      } catch (_) { /* noop */ }
    };
    const t = setTimeout(check, 1500);
    document.addEventListener("visibilitychange", check);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", check); };
  }, [isPro, token]);

  // In-app MoMo lands while the Pro is anywhere in the app.
  useEffect(() => {
    if (!isPro || !token || !user?.id) return undefined;
    let ws = null;
    let retry = 0;
    let stop = false;
    const connect = () => {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "").replace(/^http/, "ws");
      ws = new WebSocket(`${base}/api/ws/${user.id}?token=${token}`);
      ws.onmessage = (evt) => {
        try {
          const d = JSON.parse(evt.data);
          if (d.type === "withdrawal_received") {
            celebratePayment({ key: `w:${d.withdrawal_id}`, amount: d.amount, method: "withdrawal", who: d.to });
            axios.post(`${API}/pro/wallet/celebrations/${d.withdrawal_id}/seen?token=${token}`).catch(() => {});
            window.dispatchEvent(new Event("yorm:wallet-updated"));
            return;
          }
          if (d.type !== "payment_received" || isClaimed(d.target_id)) return;
          celebratePayment({ key: `p:${d.payment_id}`, amount: d.amount, method: d.method || "momo", who: d.customer_name });
        } catch (_) { /* noop */ }
      };
      ws.onclose = () => { if (!stop) retry = setTimeout(connect, 5000); };
    };
    connect();
    return () => { stop = true; clearTimeout(retry); try { ws?.close(); } catch (_) { /* noop */ } };
  }, [isPro, token, user?.id]);

  useEffect(() => {
    if (!shown) return undefined;
    const timers = BURST_TIMES.map((at) => setTimeout(playChaChing, at - 100));
    timers.push(setTimeout(() => setShown(null), 3600));
    return () => timers.forEach(clearTimeout);
  }, [shown]);

  if (!isPro) return null;
  return (
    <Dialog open={!!shown} onOpenChange={(o) => { if (!o) setShown(null); }}>
      <DialogContent className="max-w-lg p-0 gap-0" data-testid="payment-celebration-host">
        {shown && <PaymentCelebration key={shown.at} amount={shown.amount} method={shown.method} who={shown.who} />}
      </DialogContent>
    </Dialog>
  );
}
